import { useCallback, useEffect, useId, useState } from 'react';
import { useTranslation } from '../../../../lib/i18n';
import { useGenericBi } from '../../../../lib/use-generic-bi';
import { API_BASE } from '../../../../lib/api-base';
import { apiFetch } from '../../../../lib/api-client';
import { FEEDBACK_REACTION_GLYPH, feedbackLength, MAX_FEEDBACK_CHARS, type FeedbackReaction, type FeedbackView } from '../../../../lib/feedback-page';
import {
  canSendFeedback,
  feedbackInviteFullscreenMedia,
  feedbackInviteOutcome,
  firstPaintView,
  type FeedbackInviteCardData,
  type FeedbackInviteOutcome,
  type FeedbackInviteRequest,
} from '../../../../lib/feedback-invite-card';
import { openMediaCollection } from '../../../../lib/doc-viewer-presenter';
import { Button, Card, IconButton, Typography } from '../../ui';
import styles from './FeedbackInviteCard.module.css';

const REACTIONS: readonly FeedbackReaction[] = ['heart', 'fire', 'thumbs_up', 'thinking'];

async function call(body: FeedbackInviteRequest): Promise<FeedbackInviteOutcome> {
  try {
    const res = await apiFetch(`${API_BASE}/api/feedback-invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return feedbackInviteOutcome(res.status, await res.json().catch(() => null));
  } catch {
    return { kind: 'failed' };
  }
}

type Load = 'loading' | 'ready' | 'unavailable' | 'network';
type Send = 'idle' | 'sending' | 'failed' | 'rate_limited';

/**
 * A feedback round's invitation in the INVITEE's chat (`metadata.feedbackInviteCard`, `lib/feedback-invite-card.ts`),
 * answered in place: the design (its video plays when it has one), "<owner> would love your feedback", the four
 * reactions, a text box and Send; once sent, "Sent" with the answer and "Change my answer". On every open it reads the
 * round live, so an earlier answer — from this browser or the person's phone — shows as sent, and an expired round says so.
 *
 * Every string from the round is someone else's (the owner's name, the design's title): rendered as text only.
 *
 * Mirrors iOS `FeedbackInviteCardView` and Android `FeedbackInviteCard`.
 */
export function FeedbackInviteCard({ card, conversationId }: { card: FeedbackInviteCardData; conversationId?: string }) {
  const { t } = useTranslation();
  const { logPageClickBi } = useGenericBi();
  const answerId = useId();
  const [view, setView] = useState<Partial<FeedbackView>>(() => firstPaintView(card));
  const [load, setLoad] = useState<Load>('loading');
  const [send, setSend] = useState<Send>('idle');
  const [sent, setSent] = useState(false);
  const [answered, setAnswered] = useState(false);
  const [text, setText] = useState('');
  const [reaction, setReaction] = useState<FeedbackReaction | undefined>();

  const read = useCallback(async () => {
    setLoad('loading');
    const outcome = await call({ action: 'get', token: card.token });
    if (outcome.kind === 'ok') {
      setView(outcome.feedback);
      const mine = outcome.feedback.myReply;
      if (mine && (mine.text || mine.reaction)) {
        setText(mine.text ?? '');
        setReaction(mine.reaction);
        setAnswered(true);
        setSent(true);
      }
      setLoad('ready');
    } else setLoad(outcome.kind === 'unavailable' ? 'unavailable' : 'network');
  }, [card.token]);

  useEffect(() => {
    void read();
  }, [read]);

  const submit = async () => {
    logPageClickBi({ page_name: 'chat', button_name: 'feedback_invite_send', conversation_id: conversationId });
    setSend('sending');
    const outcome = await call({ action: 'submit', token: card.token, text: text.trim(), ...(reaction ? { reaction } : {}) });
    if (outcome.kind === 'ok') {
      setView(outcome.feedback);
      setAnswered(true);
      setSent(true);
      setSend('idle');
    } else if (outcome.kind === 'unavailable') {
      setLoad('unavailable');
      setSend('idle');
    } else setSend(outcome.kind === 'rate_limited' ? 'rate_limited' : 'failed');
  };

  const openFullscreen = () => {
    const media = feedbackInviteFullscreenMedia(view);
    if (!media) return;
    logPageClickBi({ page_name: 'chat', button_name: 'feedback_invite_fullscreen', answer: media.kind, conversation_id: conversationId });
    openMediaCollection([media], 0, { pageName: 'document_viewer', besideChat: true });
  };

  const sender = view.senderName || '';
  const title = sender ? t('feedbackPage.og.title', { senderName: sender }) : t('feedbackPage.ask.anonymous');
  const length = feedbackLength(text.trim());
  const tooLong = length > MAX_FEEDBACK_CHARS;

  return (
    <Card variant="outline" size="sm" className={styles.card} data-testid="feedback-invite-card">
      {view.previewUrl ? (
        <div className={styles.media}>
          {view.mediaUrl ? (
            <video src={view.mediaUrl} poster={view.previewUrl} controls playsInline preload="metadata" aria-label={view.assetTitle ?? undefined} />
          ) : (
            // A tap on the still opens it full screen; the corner control below is the keyboard path, for the video too.
            <img src={view.previewUrl} alt={view.assetTitle ?? ''} loading="lazy" className={styles.zoomable} onClick={openFullscreen} />
          )}
          <span className={styles.expand}>
            <IconButton size="small" variant="outlined" color="standard" onClick={openFullscreen} aria-label={t('common.expand')} data-testid="feedback-invite-expand">
              <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.5 2.5h4v4M6.5 13.5h-4v-4M13.5 2.5 9 7M2.5 13.5 7 9" />
              </svg>
            </IconButton>
          </span>
        </div>
      ) : null}
      <div className={styles.head}>
        <Typography variant="small-medium" component="h3" className={styles.title}>
          {title}
        </Typography>
        {view.assetTitle ? (
          <Typography variant="p3" color="secondary" component="p" className={styles.title}>
            {view.assetTitle}
          </Typography>
        ) : null}
      </div>

      {load === 'loading' ? (
        <Typography variant="p3" color="secondary" component="p" role="status">
          {t('feedbackInvite.loading')}
        </Typography>
      ) : null}

      {load === 'unavailable' ? (
        <div className={styles.answer} data-testid="feedback-invite-unavailable">
          <Typography variant="small-medium" component="p">
            {t('feedbackPage.unavailable.title')}
          </Typography>
          <Typography variant="p3" color="secondary" component="p">
            {t('feedbackPage.unavailable.body')}
          </Typography>
        </div>
      ) : null}

      {load === 'network' ? (
        <div className={styles.answer} data-testid="feedback-invite-network">
          <Typography variant="p3" color="secondary" component="p" role="status">
            {t('feedbackInvite.network')}
          </Typography>
          <Button size="small" variant="outlined" onClick={() => void read()}>
            {t('feedbackInvite.retry')}
          </Button>
        </div>
      ) : null}

      {load === 'ready' && sent ? (
        <div className={styles.answer} data-testid="feedback-invite-sent">
          {sender ? (
            <Typography variant="small-medium" component="p">
              {t('feedbackPage.sent.title', { senderName: sender })}
            </Typography>
          ) : null}
          <Typography variant="p3" color="secondary" component="p">
            {t('feedbackPage.sent', { senderName: sender || t('feedbackPage.someone') })}
          </Typography>
          {reaction ? (
            <Typography variant="p2" component="span">{`${FEEDBACK_REACTION_GLYPH[reaction]} ${t(`feedbackPage.reaction.${reaction}`)}`}</Typography>
          ) : null}
          {text.trim() ? (
            <Typography variant="p2" component="p" className={styles.comment}>
              {`“${text.trim()}”`}
            </Typography>
          ) : null}
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setSent(false);
              setSend('idle');
            }}
          >
            {t('feedbackPage.sent.change')}
          </Button>
        </div>
      ) : null}

      {load === 'ready' && !sent ? (
        <div className={styles.form}>
          <Typography variant="p3" color="secondary" component="span" id={`${answerId}-reactions`}>
            {t('feedbackPage.reaction.label')}
          </Typography>
          <div className={styles.reactions} role="group" aria-labelledby={`${answerId}-reactions`}>
            {REACTIONS.map((value) => {
              const on = reaction === value;
              return (
                <Button
                  key={value}
                  size="small"
                  variant={on ? 'contained' : 'outlined'}
                  color={on ? 'accent' : 'standard'}
                  aria-pressed={on}
                  data-testid={`feedback-invite-reaction-${value}`}
                  onClick={() => setReaction(on ? undefined : value)}
                >
                  {`${FEEDBACK_REACTION_GLYPH[value]} ${t(`feedbackPage.reaction.${value}`)}`}
                </Button>
              );
            })}
          </div>
          <textarea
            id={answerId}
            className={`orion-input ${styles.text}`}
            value={text}
            placeholder={t('feedbackPage.answer.placeholder')}
            aria-label={t('feedbackPage.answer.label')}
            aria-invalid={tooLong}
            onChange={(e) => setText(e.target.value)}
          />
          <div className={styles.footer}>
            <Typography variant="p3" color={tooLong ? 'strong' : 'secondary'} component="span" className={tooLong ? styles.alert : undefined}>
              {t('feedbackPage.answer.count', { count: length, max: MAX_FEEDBACK_CHARS })}
            </Typography>
            <Button
              size="small"
              color="accent"
              disabled={!canSendFeedback({ text, reaction }, send === 'sending')}
              loading={send === 'sending'}
              data-testid="feedback-invite-send"
              onClick={() => void submit()}
            >
              {answered ? t('feedbackPage.update') : t('feedbackPage.send')}
            </Button>
          </div>
          {send === 'failed' ? (
            <Typography variant="p3" color="strong" component="p" role="status" className={styles.alert}>
              {t('feedbackPage.failed')}
            </Typography>
          ) : null}
          {send === 'rate_limited' ? (
            <Typography variant="p3" color="strong" component="p" role="status" className={styles.alert}>
              {t('feedbackPage.rateLimited')}
            </Typography>
          ) : null}
        </div>
      ) : null}
    </Card>
  );
}
